import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Hash, Loader2, Volume2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useCampaignInfo, type CampaignInfoResponse } from '../api';
import { templateLabel } from '../types';

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value || <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

const LANGUAGE_LABEL: Record<string, string> = {
  'en-IN': 'English',
  'hi-IN': 'Hindi',
};

export function VoiceCampaignInfoPage() {
  const { id } = useParams<{ id: string }>();
  const info = useCampaignInfo(id ?? null);

  if (info.isLoading || !info.data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading campaign…
      </div>
    );
  }

  const data: CampaignInfoResponse = info.data;
  const c = data.campaign;
  const templateId = Number(c.templateId);
  const isTts = templateId === 7 || templateId === 8 || templateId === 9;
  const isCustomIvr = templateId === 3;
  const langLabel = c.language ? LANGUAGE_LABEL[c.language] ?? c.language : '';

  return (
    <ScrollPage>
      <PageHeader
        title={c.campaignName || `Campaign #${id}`}
        description={`${templateLabel(templateId)} · ID #${id}`}
        actions={
          <Button asChild variant="outline">
            <Link to="/voice-campaign-summary">
              <ArrowLeft className="h-4 w-4" />
              Back to campaigns
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
          </CardHeader>
          <CardContent>
            <dl>
              <DataRow label="Name" value={c.campaignName} />
              <DataRow label="Type" value={templateLabel(templateId)} />
              {!isTts && !isCustomIvr && (
                <DataRow label="Valid DTMF" value={c.dtmf} />
              )}
              {c.repeatMenu && (
                <DataRow label="Repeat menu" value={c.repeatMenu} />
              )}
              {!isCustomIvr && (
                <DataRow label="Menu wait time" value={c.menuWaitTime} />
              )}
              {!isCustomIvr && (
                <DataRow
                  label="Retries"
                  value={`${c.retries ?? 0} attempt(s)${
                    c.retryInterval ? ` · ${Math.round(Number(c.retryInterval) / 60)} min apart` : ''
                  }`}
                />
              )}
              {isTts && (
                <>
                  <DataRow label="Language" value={langLabel} />
                  <DataRow label="Voice gender" value={c.gender} />
                </>
              )}
              {isCustomIvr && (
                <DataRow
                  label="Custom IVR"
                  value={
                    data.ivrList?.[0]?.ivrName
                      ? `${data.ivrList[0].ivrName} (#${data.ivrList[0].ivrId})`
                      : '—'
                  }
                />
              )}
              <DataRow label="Scheduled" value={c.scheduleTime} />
              <DataRow label="Started" value={c.startTime} />
              <DataRow label="Ended" value={c.endTime} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Routing</CardTitle>
            <CardDescription>
              Locations and CLIs the campaign dialed from.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                Locations ({data.locationList?.length ?? 0})
              </div>
              {(data.locationList ?? []).length === 0 ? (
                <div className="mt-1 text-sm text-muted-foreground">—</div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(data.locationList ?? []).map((l, i) => (
                    <Badge key={i} variant="outline" className="font-normal">
                      {l.locationName || l.location}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                CLIs ({data.cliList?.length ?? 0})
              </div>
              {(data.cliList ?? []).length === 0 ? (
                <div className="mt-1 text-sm text-muted-foreground">
                  Shared pool
                </div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(data.cliList ?? []).map((c, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="font-mono text-xs"
                    >
                      {c.cli}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {(data.campaignPrompts ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Voice files</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Category</th>
                    <th className="px-3 py-2 text-left font-medium">File</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.campaignPrompts ?? []).map((p, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 capitalize">
                        {p.promptCategory || p.promptType || '—'}
                      </td>
                      <td className="px-3 py-2">
                        {p.fileName || p.promptName || `#${p.promptId ?? ''}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {(data.campaignAgents ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Patch agents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Agent group</th>
                    <th className="w-32 px-3 py-2 text-left font-medium">DTMF</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.campaignAgents ?? []).map((a, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2">{a.groupName || `Group #${a.groupId}`}</td>
                      <td className="px-3 py-2 font-mono text-xs">{a.agentDtmf}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {isTts && (data.ttsRows ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>TTS sequence</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-2">
              {(data.ttsRows ?? []).map((row, i) => {
                const isPrompt = row.ttsType === 'prompt';
                const Icon = isPrompt ? Volume2 : Hash;
                return (
                  <li key={i}>
                    <div
                      className={cn(
                        'flex items-center gap-3 rounded-lg border p-3',
                        isPrompt
                          ? 'border-sky-200 bg-sky-50/60'
                          : 'border-violet-200 bg-violet-50/60',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white',
                          isPrompt ? 'bg-sky-500' : 'bg-violet-500',
                        )}
                      >
                        {row.seq ?? i + 1}
                      </span>
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            isPrompt
                              ? 'bg-sky-100 text-sky-900'
                              : 'bg-violet-100 text-violet-900',
                          )}
                        >
                          <Icon className="h-3 w-3" />
                          {isPrompt ? 'Prompt' : 'Variable'}
                        </span>
                        <span className="truncate text-sm font-medium text-foreground">
                          {isPrompt ? row.value : `{${row.value}}`}
                        </span>
                        {!isPrompt && row.variableType && (
                          <span className="rounded border border-violet-300 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-violet-700">
                            {row.variableType}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}

      {(data.campaignSms ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>SMS triggers</CardTitle>
            <CardDescription>
              SMS gateway endpoints fired on call events.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Event</th>
                    <th className="px-3 py-2 text-left font-medium">URL</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.campaignSms ?? []).map((s, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 capitalize">
                        {s.smsType || '—'}
                      </td>
                      <td className="break-all px-3 py-2 font-mono text-xs">
                        {s.url || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </ScrollPage>
  );
}
